import { PayloadAction, useLocalSlice } from "@/Common/CustomHooks/useLocalSlice";
import { CreateFlowsheetTemplateScope } from "../Api/Contracts/CreateFlowsheetTemplate";
import { CareElementOperatorType } from "../Models/CareElementOperatorType";
import { CareElementConfig } from "../Models/CareElements/CareElementConfig";
import { CareElementType } from "../Models/CareElements/CareElementType";
import { ConfigureFlowsheetModalStepEnum } from "../Models/CareElements/ConfigureFlowsheetModalStepEnum";
import { defaultConfiguration } from "../Models/CareElements/DefaultCareComponentConfig";
import { FlowsheetConfig } from "../Models/CareElements/FlowsheetConfig";
import { LabResultsCareElementConfig } from "../Models/CareElements/LabResultsCareElementConfig";
import { ReviewedCareElementConfig } from "../Models/CareElements/ReviewedCareElementConfig";
import { VitalSignsCareElementConfig } from "../Models/CareElements/VitalSignsCareElementConfig";
import { FlowsheetTemplateScope } from "../Models/FlowsheetTemplate";
import { ITargetRange } from "../Models/ITargetRange";

/**
 * Represents the state used in the flowsheet configuration.
 */
interface IUseConfigureSliceState {
    /**
     * Represents the current step in the flowsheet configuration.
     * This determines which part of the modal is currently active.
     */
    configurationStep: ConfigureFlowsheetModalStepEnum;

    /**
     * Stores the configuration for care components in the flowsheet.
     */
    careComponentsConfig: FlowsheetConfig;

    /**
     * Indicates whether the "Apply Changes" checkbox is checked.
     * This is used to toggle the application of changes to the flowsheet.
     */
    isApplyChangesChecked: boolean;

    /**
     * The name of the template being configured.
     */
    templateName: string;

    /**
     * The scope of the template (e.g., PatientTemplate or other scopes).
     */
    templateScope: CreateFlowsheetTemplateScope | null;

    /**
     * The name of the current template being edited.
     */
    currentTemplateName: string;

    /**
     * The scope of the current template being edited.
     */
    currentTemplateScope: FlowsheetTemplateScope | null;

    /**
     * Indicates whether the creation of template is loading.
     */
    isLoadingCreation: boolean;

    /**
     * Contains a record of care elements that have invalid configurations.
     */
    invalidCareElements: Record<string, true>;
}

/**
 * Represents the actions that can be performed in the flowsheet configuration.
 */
interface IUseConfigureSliceResultActions {
    /**
     * Updates the current step in the flowsheet configuration.
     */
    setConfigurationStep: (step: ConfigureFlowsheetModalStepEnum) => void;

    /**
     * Updates the care components configuration for the flowsheet.
     */
    setCareComponentsConfig: (config: FlowsheetConfig) => void;

    /**
     * Toggles the "Apply Changes" checkbox state.
     */
    setApplyChangesChecked: (isChecked: boolean) => void;

    /**
     * Updates the name of the template being configured.
     */
    setTemplateName: (name: string) => void;

    /**
     * Updates the scope of the template (e.g., PatientTemplate).
     */
    setTemplateScope: (scope: CreateFlowsheetTemplateScope) => void;

    /**
     * Updates the state of the current template name after fetching the template.
     */
    setCurrentTemplateName: (scope: string) => void;

    /**
     * Updates the state of the current template scope after fetching the template.
     */
    setCurrentTemplateScope: (scope: FlowsheetTemplateScope) => void;

    /**
     * Updates the state of the loading of template creation.
     */
    setIsLoadingCreation: (isDisabled: boolean) => void;

    /**
     * Set the expanded state of a care component section.
     */
    setCareComponentSectionExpandedState: (payload: { careComponentName: string, isOpen: boolean }) => void;

    /**
     * Handles the action of linking or unlinking a care component.
     */
    setCareComponentActiveState: (payload: { careComponentName: string, isActivated: boolean }) => void;

    /**
     * Handles the action of removing a care element from a care component.
     */
    removeCareElementFromCareComponent: (payload: { careComponentName: string, careElementName: string }) => void;

    /**
     * Handles the action of adding a care element to a care component.
     */
    addCareElementToCareComponent: (payload: { careComponentName: string, careElementName: string }) => void;

    /**
     * Toggles the state of the "Apply Changes" checkbox.
     */
    toggleIsApplyChangesChecked: () => void;

    /**
     * Sets the care elements configuration for a specific care component.
     */
    editCareElementConfig: (payload: { careComponentName: string, careElementName: string, careElementConfig: CareElementConfig, onValidate?: (invalidConfiguration: boolean) => void }) => void;
}

type IUseConfigureSliceResult = [IUseConfigureSliceState, IUseConfigureSliceResultActions ];

const useConfigureSlice = (initialState: IUseConfigureSliceState): IUseConfigureSliceResult => useLocalSlice({
    initialState,
    reducers: {
        setConfigurationStep: (state, action: PayloadAction<ConfigureFlowsheetModalStepEnum>) => {
            state.configurationStep = action.payload;
        },
        setCareComponentsConfig: (state, action: PayloadAction<FlowsheetConfig>) => {
            state.careComponentsConfig = action.payload;
        },
        setApplyChangesChecked: (state, action: PayloadAction<boolean>) => {
            state.isApplyChangesChecked = action.payload;
        },
        setTemplateName: (state, action: PayloadAction<string>) => {
            state.templateName = action.payload;
        },
        setTemplateScope: (state, action: PayloadAction<CreateFlowsheetTemplateScope>) => {
            state.templateScope = action.payload;
            if (action.payload === CreateFlowsheetTemplateScope.Patient) {
                if (state.currentTemplateScope === FlowsheetTemplateScope.Patient)
                    state.templateName = state.currentTemplateName;
                else
                    state.templateName = `${state.currentTemplateName} - ${patientStore.getCurrentPatient().GetFormattedName()}`;
            }
            else {
                state.templateName = "";
            }
        },
        setCurrentTemplateName: (state, action: PayloadAction<string>) => {
            state.currentTemplateName = action.payload;
        },
        setCurrentTemplateScope: (state, action: PayloadAction<FlowsheetTemplateScope>) => {
            state.currentTemplateScope = action.payload;
        },
        setIsLoadingCreation: (state, action: PayloadAction<boolean>) => {
            state.isLoadingCreation = action.payload;
        },
        setCareComponentActiveState: (state, action: PayloadAction<{ careComponentName: string, isActivated: boolean }>) => {
            const config = state.careComponentsConfig.CareComponents.find(c => c.Name === action.payload.careComponentName);
            if (config) {
                config.IsActive = action.payload.isActivated;
                config.Expanded = action.payload.isActivated;
            }
        },
        setCareComponentSectionExpandedState: (state, action: PayloadAction<{ careComponentName: string, isOpen: boolean }>) => {
            const config = state.careComponentsConfig.CareComponents.find(c => c.Name === action.payload.careComponentName);
            if (config) {
                config.Expanded = action.payload.isOpen;
            }
        },
        removeCareElementFromCareComponent: (state, action: PayloadAction<{ careComponentName: string, careElementName: string }>) => {
            const { careComponentName, careElementName } = action.payload;

            state.careComponentsConfig = {
                ...state.careComponentsConfig,
                CareComponents: state.careComponentsConfig.CareComponents.map(component =>
                    component.Name === action.payload.careComponentName
                        ? {
                            ...component,
                            CareElementsConfig: component.CareElementsConfig.filter(
                                element => element.Name !== action.payload.careElementName
                            )
                        }
                        : component
                )
            };
            const key = `${careComponentName}::${careElementName}`;
            state.invalidCareElements = Object.fromEntries(
                Object.entries(state.invalidCareElements).filter(([k]) => k !== key)
            );
        },
        addCareElementToCareComponent: (state, action: PayloadAction<{ careComponentName: string, careElementName: string }>) => {
            const elementToAdd = defaultConfiguration.CareComponents
                .find(c => c.Name === action.payload.careComponentName)
                ?.CareElementsConfig
                .find(e => e.Name === action.payload.careElementName);

            if (!elementToAdd) {
                console.warn(`Care element with name ${action.payload.careElementName} not found in component ${action.payload.careComponentName}`);
                return;
            }

            state.careComponentsConfig = {
                ...state.careComponentsConfig,
                CareComponents: state.careComponentsConfig.CareComponents.map(component =>
                    component.Name === action.payload.careComponentName
                        ? {
                            ...component,
                            CareElementsConfig: [
                                ...component.CareElementsConfig,
                                elementToAdd
                            ]
                        }
                        : component
                )
            };
        },
        toggleIsApplyChangesChecked: state => {
            state.isApplyChangesChecked = !state.isApplyChangesChecked;
        },
        editCareElementConfig: (state, action: PayloadAction<{ careComponentName: string, careElementName: string, careElementConfig: CareElementConfig }>) => {
            const { careElementConfig, careElementName, careComponentName } = action.payload;

            state.careComponentsConfig = {
                ...state.careComponentsConfig,
                CareComponents: state.careComponentsConfig.CareComponents.map(component =>
                    component.Name === action.payload.careComponentName
                        ? {
                            ...component,
                            CareElementsConfig: component.CareElementsConfig.map(element =>
                                element.Name === action.payload.careElementName
                                    ? action.payload.careElementConfig
                                    : element
                            )
                        }
                        : component
                )
            };

            const { targetPresent, operatorPresent } = validateConfiguration(careElementConfig);
            const invalid = targetPresent !== operatorPresent;
            const key = `${careComponentName}::${careElementName}`;

            if (invalid) {
                state.invalidCareElements[key] = true;
            }
            else {
                state.invalidCareElements = Object.fromEntries(
                    Object.entries(state.invalidCareElements).filter(([k]) => k !== key)
                );
            }
        }
    }
});

const isTargetRangePresent = (configOperator?: number, targetRange?: ITargetRange): boolean => {
    const isTargetBetween = configOperator === CareElementOperatorType.Between;
    const lower = targetRange?.LowerBound;
    const upper = targetRange?.UpperBound;

    return isTargetBetween
        ? lower != null && upper != null
        : lower != null;
};

const isOperatorPresent = (configOperator?: number): boolean => {
    if (configOperator == null || Number.isNaN(configOperator))
        return false;

    return configOperator !== CareElementOperatorType.None;
};

const validateConfiguration = (cfg: CareElementConfig): { targetPresent: boolean; operatorPresent: boolean } => {
    switch (cfg.CareElementType) {
        case CareElementType.LabResults: {
            const config = cfg as LabResultsCareElementConfig;
            const configOperator = Number(config.Operator);
            return {
                targetPresent: isTargetRangePresent(configOperator, config.TargetRange),
                operatorPresent: isOperatorPresent(configOperator)
            };
        }
        case CareElementType.VitalSigns: {
            const config = cfg as VitalSignsCareElementConfig;
            const configOperator = Number(config.Operator);
            return {
                targetPresent: isTargetRangePresent(configOperator, config.TargetRange),
                operatorPresent: isOperatorPresent(configOperator)
            };
        }
        case CareElementType.TextReviewed:
        case CareElementType.DateOnlyReviewed:
        case CareElementType.DecimalReviewed:
        case CareElementType.IntegerReviewed:
        case CareElementType.MultiSelectReviewed: {
            const config = cfg as ReviewedCareElementConfig;
            const configOperator = Number(config.Operator);
            return {
                targetPresent: config.Target != null,
                operatorPresent: isOperatorPresent(configOperator)
            };
        }
        default:
            return { targetPresent: false, operatorPresent: false };
    }
};

export {
    IUseConfigureSliceResultActions,
    IUseConfigureSliceState,
    useConfigureSlice
};
